import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';
import type MapFloorModel from 'garaje/models/map-floor';
import type StateService from 'garaje/services/state';

import type { ModelResponse } from './route';

/**
 * Live Map V2 embed controller. Floor-plan-fe owns the toolbar UI +
 * all filter state (search, floor, resource type, neighborhoods,
 * date/time) inside the iframe. Garaje's role here reduces to:
 *   1. Compose the iframe URL — location + optional floor deep-link + IANA tz
 *   2. Retain `selectedFloorId` as a queryParam so paste-URL deep-linking
 *      into a specific floor still works.
 *
 * No postMessage bridge, no toolbar chrome, no per-filter state — those
 * all live in floor-plan-fe (see docs/superpowers/plans/2026-07-13-toolbar-migration.md
 * in the floor-plan-fe repo for the full migration plan).
 */
export default class SpacesMapsLiveNewController extends Controller {
  @service declare state: StateService;

  declare model: ModelResponse;

  queryParams = ['selectedFloorId'];

  @tracked selectedFloorId: string | null = null;

  get sortedMapFloors(): MapFloorModel[] {
    const floors = this.model?.mapFloors ?? [];
    return [...floors].sort((a, b) => (a.ordinality || 0) - (b.ordinality || 0)).filter((f) => f.rasterImageUrl);
  }

  /**
   * Iframe URL. Only the deep-link + auth-context params live here;
   * everything filter-related is managed inside floor-plan-fe.
   *   - `location`: current org location (required by FPS render calls)
   *   - `floor` (optional): deep-link into a specific floor when
   *     `selectedFloorId` was persisted via queryParams
   *   - `tz` (optional): the location's IANA timezone name (e.g.
   *     "America/Los_Angeles"). Floor-plan-fe's DatePicker uses this
   *     with date-fns-tz's `fromZonedTime` so wall-clock interpretation
   *     matches garaje's own maps — no timezone drift possible.
   */
  get floorPlanFeUrl(): string {
    const base = config.floorPlanFeUrl;
    if (!base) return '';

    const locationId = this.state.currentLocation?.id;
    if (!locationId) return base;

    const tz = this.state.currentLocation?.timezone;

    // Tuple array so the params serialize via `new URLSearchParams(entries)`
    // rather than `URLSearchParams.set()` — the latter trips ember-lint's
    // `use-ember-get-and-set` false positive.
    const entries: [string, string][] = [['location', String(locationId)]];
    if (this.selectedFloorId) entries.push(['floor', this.selectedFloorId]);
    if (tz) entries.push(['tz', tz]);

    return `${base}?${new URLSearchParams(entries).toString()}`;
  }
}
